import { useId, useState } from 'react';
import { Button, FormField, SelectableChip, TextField, Textarea } from '../../components';
import { notify } from '../../services/notify';
import {
  USER_MESSAGE_FORM_ID,
  USER_MESSAGE_SMS_MAX_CHARS,
  type AdminProfile,
  type UserMessageChannel,
  type UserMessageLang,
} from './api';
import { useSendUserMessage, useTranslateUserMessage } from './queries';

const CHANNEL_LABEL: Record<UserMessageChannel, string> = { email: 'Email', sms: 'SMS' };

/** The Form ID the tab starts with: VITE_TALLY_DEFAULT_FORM_ID, the main /connect form. */
function defaultFormId(): string {
  return (import.meta.env.VITE_TALLY_DEFAULT_FORM_ID ?? '').trim();
}
const LANG_LABEL: Record<UserMessageLang, string> = { en: 'English', 'es-MX': 'Spanish' };

/**
 * A message to this person — the Message tab of the Chef and Eater dialogs
 * (`POST /admin/send-user-message`). Only rendered for `user.message`.
 *
 * One channel per send. The message goes out as written, with a link to the
 * www Connect page that opens the Tally form `formId` for this person.
 * Translate replaces the text with its translation, to read and edit before
 * sending; nothing is translated on send. Form ID starts at (and returns to,
 * after a send) VITE_TALLY_DEFAULT_FORM_ID.
 */
export function UserMessagePanel({ profile, noun }: { profile: AdminProfile; noun: string }) {
  const messageId = useId();
  const formId = useId();
  const send = useSendUserMessage();
  const translate = useTranslateUserMessage();

  const hasEmail = !!profile.email;
  const hasPhone = !!profile.phoneE164;
  const [message, setMessage] = useState('');
  const [channel, setChannel] = useState<UserMessageChannel | null>(hasEmail ? 'email' : hasPhone ? 'sms' : null);
  const [lang, setLang] = useState<UserMessageLang>('en');
  const [form, setForm] = useState(defaultFormId);
  const [formTouched, setFormTouched] = useState(false);

  const smsTooLong = channel === 'sms' && message.length > USER_MESSAGE_SMS_MAX_CHARS;
  const formValid = USER_MESSAGE_FORM_ID.test(form);
  const valid = !!message.trim() && !!channel && formValid && !smsTooLong;

  const onTranslate = () =>
    translate.mutate(
      { text: message, lang },
      {
        onSuccess: (text) => {
          setMessage(text);
          notify.success(`Translated to ${LANG_LABEL[lang]}. Review it before sending.`);
        },
      },
    );

  const onSend = () => {
    if (!channel) return;
    send.mutate(
      { profileId: profile.id, message, channel, lang, formId: form },
      {
        onSuccess: (res) => {
          if (res.status === 'sent') notify.success('Message sent.');
          else notify.info(`${CHANNEL_LABEL[channel]} sending is turned off, so nothing was sent.`);
          setMessage('');
          setForm(defaultFormId());
          setFormTouched(false);
        },
      },
    );
  };

  return (
    <div className="flex flex-col gap-4">
      <FormField
        label="Message"
        htmlFor={messageId}
        required
        error={smsTooLong ? `An SMS message can be at most ${USER_MESSAGE_SMS_MAX_CHARS} characters.` : undefined}
        hint="Sent as written, with a link to connect. Include your greeting and sign-off."
      >
        <Textarea
          id={messageId}
          surface="white"
          rows={3}
          placeholder={`Write a message to this ${noun}…`}
          value={message}
          invalid={smsTooLong}
          showCount={channel === 'sms'}
          maxLength={channel === 'sms' ? USER_MESSAGE_SMS_MAX_CHARS : undefined}
          onChange={(e) => setMessage(e.target.value)}
        />
      </FormField>

      <FormField label="Channel" required>
        <div className="flex flex-wrap gap-[7px]" role="group" aria-label="Channel">
          <SelectableChip
            active={channel === 'email'}
            disabled={!hasEmail}
            title={hasEmail ? undefined : `This ${noun} has no email address.`}
            onClick={() => setChannel('email')}
          >
            {CHANNEL_LABEL.email}
          </SelectableChip>
          <SelectableChip
            active={channel === 'sms'}
            disabled={!hasPhone}
            title={hasPhone ? undefined : `This ${noun} has no phone number.`}
            onClick={() => setChannel('sms')}
          >
            {CHANNEL_LABEL.sms}
          </SelectableChip>
        </div>
      </FormField>

      <FormField label="Language" required>
        <div className="flex flex-wrap items-center gap-[7px]">
          <div className="flex gap-[7px]" role="group" aria-label="Language">
            {(Object.keys(LANG_LABEL) as UserMessageLang[]).map((l) => (
              <SelectableChip key={l} active={lang === l} onClick={() => setLang(l)}>
                {LANG_LABEL[l]}
              </SelectableChip>
            ))}
          </div>
          <Button
            variant="ghost-dark"
            size="sm"
            disabled={!message.trim()}
            loading={translate.isPending}
            onClick={onTranslate}
          >
            Translate to {LANG_LABEL[lang]}
          </Button>
        </div>
      </FormField>

      <FormField
        label="Form ID"
        htmlFor={formId}
        required
        error={formTouched && form && !formValid ? 'Up to 12 letters or digits, e.g. kbaJpj.' : undefined}
        hint="The Tally form the link opens."
      >
        <TextField
          id={formId}
          surface="white"
          placeholder="kbaJpj"
          value={form}
          invalid={formTouched && !!form && !formValid}
          onChange={(e) => setForm(e.target.value.trim())}
          onBlur={() => setFormTouched(true)}
        />
      </FormField>

      <div className="flex justify-end">
        <Button size="sm" disabled={!valid} loading={send.isPending} onClick={onSend}>
          Send
        </Button>
      </div>
    </div>
  );
}
